import { useCallback } from 'react';

import { useTrackCanvasMediaImported } from '@air/analytics';
import { useToasts } from '@air/provider-toast';

import { useEditCanvasStore } from '../store';
import { type AppNode, type EditUploadContentType } from '../types';
import { correctNodeDimensions } from '../utils/correctNodeDimensions';
import { getCompatibleImageUrl } from '../utils/getCompatibleImage';
import { getLocalImageDimensions } from '../utils/getLocalImageDimensions';
import { computeRowAwareAppendPosition } from '../utils/layout/nodeLayout';
import { isAcceptedMimeType } from '../utils/uploadMimeTypes';
import { commands, useCommandHistory } from './commands';
import { useNodeLayout } from './useNodeLayout';
import { useUploadAndPreload } from './useUploadAndPreload';
import { useZoomToFit } from './useZoomToFit';

const FALLBACK_IMAGE_SIZE = 1024;

type ValidFile = {
  file: File;
  contentType: EditUploadContentType;
  dimensions: { height: number; width: number };
  dimensionsKnown: boolean;
};

type FileMeta = ValidFile & {
  blobUrl: string;
  nodeId: string;
  position: { x: number; y: number };
  previewId: string;
  zIndex: number;
};

/**
 * Returns a callback that accepts raw File objects, uploads them, and adds
 * them as image nodes on the canvas with optimistic preview placeholders.
 *
 * This is the core upload-to-canvas logic shared by:
 * - {@link useUploadImageToCanvas} (toolbar file picker)
 * - {@link EditCanvasFileDropzone} (OS file drag-and-drop)
 */
export function useDropFilesToCanvas() {
  const execute = useCommandHistory((state) => state.execute);
  const { showToast } = useToasts();
  const { getContentNodeRects, getGap, getMaxRowWidth } = useNodeLayout();
  const { uploadAndPreload } = useUploadAndPreload();
  const { trackCanvasMediaImported } = useTrackCanvasMediaImported();
  const { zoomToFit } = useZoomToFit();

  const dropFilesToCanvas = useCallback(
    async (files: File[]) => {
      const validFiles: ValidFile[] = [];
      let skippedCount = 0;

      for (const file of files) {
        if (!isAcceptedMimeType(file.type)) {
          skippedCount++;
          continue;
        }

        const dimensions = await getLocalImageDimensions(file);
        validFiles.push({
          contentType: file.type,
          dimensions: dimensions ?? { height: FALLBACK_IMAGE_SIZE, width: FALLBACK_IMAGE_SIZE },
          dimensionsKnown: dimensions !== null,
          file,
        });
      }

      if (validFiles.length === 0) {
        showToast('No supported images selected', { color: 'red' });
        return;
      }

      const virtualRects = getContentNodeRects();
      const maxZIndex = useEditCanvasStore.getState().nodes.reduce((max, n) => Math.max(max, n.zIndex ?? 0), -1);
      const gap = getGap();
      const maxRowWidth = getMaxRowWidth();

      const fileMeta: FileMeta[] = [];

      for (let i = 0; i < validFiles.length; i++) {
        const vf = validFiles[i];
        const { position } = computeRowAwareAppendPosition({
          allNodes: virtualRects,
          gap,
          maxRowWidth,
          newNodeSize: vf.dimensions,
        });

        // Accumulate into virtualRects so each subsequent image positions after the previous ones.
        virtualRects.push({
          height: vf.dimensions.height,
          id: crypto.randomUUID(),
          width: vf.dimensions.width,
          x: position.x,
          y: position.y,
        });

        fileMeta.push({
          ...vf,
          blobUrl: URL.createObjectURL(vf.file),
          nodeId: crypto.randomUUID(),
          position,
          previewId: crypto.randomUUID(),
          zIndex: maxZIndex + 1 + i,
        });
      }

      const previewNodes: AppNode[] = fileMeta.map((meta) => ({
        data: { blobUrl: meta.blobUrl, fileName: meta.file.name },
        id: meta.previewId,
        position: meta.position,
        selected: true,
        style: { height: meta.dimensions.height, width: meta.dimensions.width },
        type: 'imageUploadPreview',
        zIndex: meta.zIndex,
      }));

      const previewIds = new Set(previewNodes.map((n) => n.id));

      useEditCanvasStore.getState().addNodes(previewNodes);

      try {
        zoomToFit({ nodeIds: previewNodes.map((n) => n.id) });

        const results = await uploadAndPreload(fileMeta.map((m) => ({ contentType: m.contentType, file: m.file })));

        const finalNodes: AppNode[] = [];
        let uploadFailCount = 0;

        for (let i = 0; i < fileMeta.length; i++) {
          const result = results[i];
          const meta = fileMeta[i];

          if (result.status === 'fulfilled') {
            finalNodes.push({
              data: {
                fileName: meta.file.name,
                media: { image: getCompatibleImageUrl(result.cdnUrl), original: result.cdnUrl },
                source: 'manual-edit',
                type: 'image',
              },
              id: meta.nodeId,
              position: meta.position,
              style: { height: meta.dimensions.height, width: meta.dimensions.width },
              type: 'image',
              zIndex: meta.zIndex,
            });
          } else {
            uploadFailCount++;
          }
        }

        // Single command so all uploaded images are one undo/redo entry.
        if (finalNodes.length > 0) {
          execute(commands.addNodes(finalNodes, { removeNodeIds: Array.from(previewIds) }));

          const canvasId = useEditCanvasStore.getState().canvasId;

          if (canvasId) {
            trackCanvasMediaImported({
              canvas_id: canvasId,
              number_of_media: finalNodes.length,
              source: 'desktop',
            });
          }
        }

        // Fire-and-forget: correct dimensions for files the browser couldn't decode locally
        const nodesNeedingDimensions = fileMeta
          .map((m, i) => ({ meta: m, result: results[i] }))
          .filter(
            (entry): entry is { meta: FileMeta; result: { status: 'fulfilled'; cdnUrl: string } } =>
              !entry.meta.dimensionsKnown && entry.result.status === 'fulfilled',
          );

        if (nodesNeedingDimensions.length > 0) {
          correctNodeDimensions(
            nodesNeedingDimensions.map(({ meta, result }) => ({ cdnUrl: result.cdnUrl, nodeId: meta.nodeId })),
            finalNodes.map((n) => n.id),
          );
        }

        const totalFailed = skippedCount + uploadFailCount;

        if (finalNodes.length === 0) {
          showToast('Failed to upload images', { color: 'red' });
          useEditCanvasStore.setState((state) => ({
            nodes: state.nodes.filter((n) => !previewIds.has(n.id)),
          }));
        } else if (totalFailed > 0) {
          showToast(`${totalFailed} of ${files.length} files failed to upload`, { color: 'red' });
        } else if (finalNodes.length === 1) {
          showToast('Image added to canvas');
        } else {
          showToast(`${finalNodes.length} images added to canvas`);
        }
      } catch {
        showToast('Failed to upload images', { color: 'red' });
        // Remove preview nodes on failure (not synced, so bypass removeNodes to avoid queue)
        useEditCanvasStore.setState((state) => ({
          nodes: state.nodes.filter((n) => !previewIds.has(n.id)),
        }));
      }
    },
    [
      execute,
      getContentNodeRects,
      getGap,
      getMaxRowWidth,
      showToast,
      trackCanvasMediaImported,
      uploadAndPreload,
      zoomToFit,
    ],
  );

  return { dropFilesToCanvas };
}
